import { useState } from 'react';
import ProductLookup from '../components/ProductLookup.jsx';
import Lookup from '../components/Lookup.jsx';
import { api, money } from '../api.js';
import NumBox from '../components/NumBox.jsx';

/**
 * Поправка на проведена доставка.
 *
 * Случаят: стоката е дошла без фактура, въведена е с цена наизуст и вече
 * е продадена. На другия ден идва фактурата — с други числа и с още
 * стоки по нея.
 *
 * Затова се редактират количество и цена, добавят се редове и се
 * попълват данните на фактурата. Ред, от чиято партида вече е продавано,
 * не може да се махне — сървърът го отказва и съобщението излиза тук.
 *
 * Цената се въвежда С ДДС, освен ако доставката не е записана с цени по
 * фактура — тогава признакът стои вдигнат и данъкът се начислява отгоре.
 */

/**
 * С цени без ДДС ли е записана доставката.
 *
 * Никъде не се пази признак, но самите редове го издават: сборът на
 * количество по цена пада или върху основата, или върху сумата с данъка.
 * Без тази проверка поправката на доставка, разчетена от фактура, би
 * свалила по една пета от всеки ред.
 */
function looksNet(items) {
  const given = items.reduce((s, it) => s + Number(it.quantity || 0) * Number(it.price || 0), 0);
  const net = items.reduce((s, it) => s + Number(it.line_subtotal || 0), 0);
  const gross = items.reduce((s, it) => s + Number(it.line_total || 0), 0);
  if (Math.abs(net - gross) < 0.005) return false;          // няма ДДС — няма разлика
  return Math.abs(given - net) < Math.abs(given - gross);
}
export default function PurchaseEdit({ doc, onDone, onCancel }) {
  const [rows, setRows] = useState(() => doc.items.map((it) => ({
    key: `r${it.id}`,
    id: it.id,
    productId: it.product_id,
    description: it.description,
    unit: it.unit,
    batchNo: it.batch_no,
    quantity: String(Number(it.quantity)),
    price: String(Number(it.price)),
    vatRate: Number(it.vat_rate),
  })));
  const [pricesNet, setPricesNet] = useState(() => looksNet(doc.items));
  const [transport, setTransport] = useState(
    doc.transport_cost != null && Number(doc.transport_cost) !== 0
      ? String(Number(doc.transport_cost)) : '');
  const [discount, setDiscount] = useState(
    doc.invoice_discount != null && Number(doc.invoice_discount) !== 0
      ? String(Number(doc.invoice_discount)) : '');
  const [invoiceNo, setInvoiceNo] = useState(doc.invoice_no ?? '');
  // Смяна на доставчика (06.10.2026) — плащането се мести заедно с нея.
  const [dostavchik, setDostavchik] = useState(
    doc.partner_id ? { id: doc.partner_id, name: doc.partner_name } : null);
  const [invoiceDate, setInvoiceDate] = useState(
    doc.invoice_date ? String(doc.invoice_date).slice(0, 10) : '');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const patch = (key, p) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...p } : r)));

  /** Свързва реда с друга стока — количеството и цената остават. */
  async function relink(key, productId) {
    if (!productId) { patch(key, { picking: false }); return; }
    try {
      const p = await api.get(`/api/products/${productId}`);
      patch(key, {
        productId: p.id,
        description: p.name,
        unit: p.unit,
        batchNo: null,
        changed: true,
        picking: false,
      });
    } catch (err) {
      setError(err.message);
      patch(key, { picking: false });
    }
  }

  // `Lookup` подава номера на стоката, не самата стока — мярката,
  // ставката и последната доставна цена се четат отделно.
  async function addProduct(productId) {
    if (!productId) return;
    try {
      const p = await api.get(`/api/products/${productId}`);
      // Същият ред както при въвеждане: последната доставка от този
      // доставчик, после от когото и да е, накрая картонът на стоката.
      let price = p.purchase_price;
      try {
        const r = await api.get(
          `/api/products/last-purchases?productIds=${p.id}`
          + (doc.partner_id ? `&partnerId=${doc.partner_id}` : '')
        );
        const hist = r.items?.[p.id];
        price = hist?.same?.price ?? hist?.other?.price ?? p.purchase_price;
      } catch { /* остава каквото знаем */ }

      setRows((rs) => [...rs, {
        key: `n${p.id}-${Date.now()}`,
        id: null,
        productId: p.id,
        description: p.name,
        unit: p.unit,
        batchNo: null,
        quantity: '1',
        price: price != null ? String(Number(price)) : '0',
        vatRate: Number(p.vat_rate ?? 20),
      }]);
    } catch (err) {
      setError(err.message);
    }
  }

  /*
   * Сумите на екрана се смятат както ги смята сървърът: отстъпката за
   * цялата фактура сваля основата, а данъкът се начислява върху
   * свалената. Иначе показаното и записаното се разминават.
   */
  const round2 = (n) => Math.round(n * 100) / 100;
  const byRate = new Map();
  for (const r of rows) {
    const line = round2(Number(r.quantity || 0) * Number(r.price || 0));
    const rate = Number(r.vatRate || 0);
    const net = pricesNet ? line : round2(line / (1 + rate / 100));
    byRate.set(rate, round2((byRate.get(rate) ?? 0) + net));
  }
  const cut = Math.abs(Number(discount) || 0);
  const groups = [...byRate.entries()];
  const rawNet = round2(groups.reduce((s, [, n]) => s + n, 0));
  const take = Math.min(cut, rawNet);
  let left = take;
  let netTotal = 0;
  let vat = 0;
  for (const [i, [rate, groupNet]] of groups.entries()) {
    const share = rawNet > 0
      ? (i === groups.length - 1 ? left : round2((take * groupNet) / rawNet))
      : 0;
    left = round2(left - share);
    const groupBase = round2(groupNet - share);
    netTotal = round2(netTotal + groupBase);
    vat = round2(vat + round2((groupBase * rate) / 100));
  }
  const lines = netTotal;
  const total = round2(netTotal + vat);
  // Транспортът вдига себестойността, отстъпката я сваля. Сумата на
  // документа не се пипа — затова числото стои отделно.
  const extraNet = Number(transport || 0) - Number(discount || 0);

  async function save() {
    if (!dostavchik) { setError('Изберете доставчик'); return; }
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/documents/${doc.id}`, {
        invoiceNo: invoiceNo.trim() || null,
        invoiceDate: invoiceDate || null,
        partnerId: dostavchik && Number(dostavchik.id) !== Number(doc.partner_id)
          ? Number(dostavchik.id) : undefined,
        pricesNet,
        transportCost: Number(transport || 0),
        invoiceDiscount: Number(discount || 0),
        items: rows.map((r) => ({
          id: r.id ?? undefined,
          productId: r.productId,
          description: r.description,
          unit: r.unit,
          quantity: Number(r.quantity || 0),
          price: Number(r.price || 0),
          vatRate: r.vatRate,
        })),
      });
      onDone();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <div className="card-head">Поправка на доставката</div>

      <div className="card-body" style={{ paddingBottom: 8 }}>
        <p className="hint" style={{ marginTop: 0 }}>
          Смяната на доставната цена стига и до вече продаденото — себестойността
          на продажбите от тази партида се преизчислява.
        </p>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {/* div, не label: в Lookup има бутон „×“ (виж quick-period-label-bug). */}
          <div className="field" style={{ minWidth: 260 }}>
            Доставчик
            <Lookup
              url="/api/partners"
              value={dostavchik?.id ?? null}
              label={dostavchik?.name ?? ''}
              placeholder="търсене по име или ЕИК"
              onPick={(id, name) => setDostavchik(id ? { id, name } : null)}
            />
          </div>
          <label className="field">
            Номер на фактурата
            <input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
          </label>
          <label className="field">
            Дата на фактурата
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} />
          </label>
          <label className="field" style={{ alignSelf: 'end' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={pricesNet}
                onChange={(e) => setPricesNet(e.target.checked)} />
              цените са без ДДС
            </span>
          </label>
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="num" style={{ width: 34 }}>№</th>
              <th>Наименование</th>
              <th>Партида</th>
              <th className="num" style={{ width: 110 }}>Количество</th>
              <th className="num" style={{ width: 130 }}>
                {pricesNet ? 'Ед. цена без ДДС' : 'Ед. цена с ДДС'}
              </th>
              <th className="num" style={{ width: 70 }}>ДДС</th>
              <th className="num" style={{ width: 120 }}>Сума</th>
              <th style={{ width: 40 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, index) => (
              <tr key={r.key}>
                <td className="num muted">{index + 1}</td>
                <td>
                  {r.picking ? (
                    <ProductLookup value={null} label=""
                      placeholder="посочи вярната стока"
                      onPick={(id) => relink(r.key, id)} />
                  ) : (
                    <>
                      {r.description}
                      {r.id ? '' : <span className="badge" style={{ marginLeft: 6 }}>нов</span>}
                      {r.changed && (
                        <span className="badge" style={{ marginLeft: 6 }}>сменена</span>
                      )}
                      {/* Разчитането може да е свързало реда с грешна
                          стока и това да се види чак след записа. */}
                      <button className="link" style={{ marginLeft: 8 }}
                        title="Свържи реда с друга стока"
                        onClick={() => patch(r.key, { picking: true })}>
                        промени
                      </button>
                    </>
                  )}
                </td>
                <td className="muted">{r.batchNo ?? '—'}</td>
                <td className="num">
                  <input className="num" type="number" step="1" min="0"
                    value={r.quantity}
                    onChange={(e) => patch(r.key, {
                      quantity: String(e.target.value).replace(/[^\d]/g, ''),
                    })} />
                </td>
                <td className="num">
                  <NumBox className="price num"
                    value={r.price}
                    onChange={(v) => patch(r.key, { price: v })} />
                </td>
                <td className="num muted">{r.vatRate}%</td>
                <td className="num">
                  {money(Number(r.quantity || 0) * Number(r.price || 0), doc.currency)}
                </td>
                <td>
                  <button className="link" title="махни реда"
                    onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>×</button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="empty">Доставката остава без редове</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="card-body">
        <label className="field" style={{ maxWidth: 460 }}>
          Добавяне на стока от фактурата
          <ProductLookup value={null} label=""
            placeholder="търсене по име или код"
            onPick={(id) => addProduct(id)} />
        </label>

        {/* Двете полета стоят там, където са и при въвеждането: над
            сумите. Операторът ги търси на същото място. */}
        <div style={{ maxWidth: 420, marginTop: 12 }}>
          {/* Като в NewDocument (27.10.2026) — без .toolbar, иначе второто
              поле застъпва първото. */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 4 }}>
            <label className="field" style={{ width: 180 }}>
              Транспорт за наша сметка
              <NumBox className="num" placeholder="0.00" style={{ width: '100%', boxSizing: 'border-box' }}
                value={transport} onChange={setTransport} />
            </label>
            <label className="field" style={{ width: 180 }}>
              Отстъпка по фактурата
              <NumBox className="num" placeholder="0.00" style={{ width: '100%', boxSizing: 'border-box' }}
                value={discount} onChange={setDiscount} />
            </label>
          </div>
          <span className="hint">
            Отстъпката е ред от фактурата и сваля сумата на доставката.
            Транспортът „за наша сметка" не я пипа — той само вдига
            себестойността. И двете се разнасят по редовете съразмерно на
            стойността им: транспортът я вдига, отстъпката я сваля.
            {extraNet !== 0 && (
              <> Върху себестойността ляга{' '}
                <b>{extraNet > 0 ? '+' : '−'}{Math.abs(extraNet).toFixed(2)} €</b>.</>
            )}
          </span>
        </div>

        <div className="totals" style={{ marginTop: 12 }}>
          <div><span>Данъчна основа</span><span>{money(lines, doc.currency)}</span></div>
          <div><span>ДДС</span><span>{money(vat, doc.currency)}</span></div>
          <div className="grand"><span>Общо</span><span>{money(total, doc.currency)}</span></div>
        </div>

        {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="primary" onClick={save} disabled={busy}>Запис на поправката</button>
          <button onClick={onCancel} disabled={busy}>Отказ</button>
        </div>
      </div>
    </div>
  );
}
